"use client";

import { useState, FormEvent } from "react";
import { API_BASE } from "../../../lib/api";

// Generic waitlist card. One email input, one submit, posts to the
// existing `/waitlist` endpoint with whatever product key the caller
// passes in. The endpoint allow-lists the product server-side
// (backend/main.py), so the value must match one of:
//   "nclex", "usmle", "lsat", "dat", "gre", "mobile_app"
// Add to the backend allow-list before introducing a new product
// here, or the POST will return 400.

type Status = "idle" | "submitting" | "subscribed" | "already" | "error";

interface WaitlistInsetProps {
  eyebrow?: string;
  title: string;
  description: string;
  product: string;
  buttonLabel?: string;
  placeholder?: string;
  inputId: string;
}

export default function WaitlistInset({
  eyebrow = "Coming soon",
  title,
  description,
  product,
  buttonLabel = "Join the list",
  placeholder = "you@example.com",
  inputId,
}: WaitlistInsetProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!value) {
      setStatus("error");
      setErrorMessage("Enter your email to join the list.");
      return;
    }
    setStatus("submitting");
    setErrorMessage("");
    try {
      const res = await fetch(`${API_BASE}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: value,
          product,
          source: "landing-page",
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: "Could not add you right now." }));
        setStatus("error");
        setErrorMessage(typeof data.detail === "string" ? data.detail : "Could not add you right now.");
        return;
      }
      const data = await res.json();
      setStatus(data.status === "already_subscribed" ? "already" : "subscribed");
      setEmail("");
    } catch {
      setStatus("error");
      setErrorMessage("Network error. Try again in a moment.");
    }
  }

  const done = status === "subscribed" || status === "already";

  return (
    <div className="rounded-2xl border border-maren-sage/50 dark:border-maren-sage/25 bg-maren-stone dark:bg-maren-charcoal-elevated/60 p-6 sm:p-7 flex flex-col h-full">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-maren-green dark:text-maren-gold">
        {eyebrow}
      </p>
      <p className="mt-2 font-display text-xl sm:text-2xl text-maren-charcoal dark:text-maren-stone leading-snug">
        {title}
      </p>
      <p className="mt-2 text-sm text-maren-charcoal/70 dark:text-maren-stone/75 leading-relaxed">
        {description}
      </p>

      <div className="mt-5 flex-1 flex flex-col justify-end">
        {done ? (
          <p className="text-sm font-semibold text-maren-green dark:text-maren-gold">
            {status === "already" ? "You are already on the list." : "You are on the list."}
          </p>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2">
            <label htmlFor={inputId} className="sr-only">
              Email
            </label>
            <input
              id={inputId}
              type="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === "error") setStatus("idle");
              }}
              placeholder={placeholder}
              disabled={status === "submitting"}
              className="flex-1 min-w-0 rounded-lg border border-maren-sage bg-white dark:bg-maren-charcoal text-maren-charcoal dark:text-maren-stone placeholder:text-maren-charcoal/40 dark:placeholder:text-maren-stone/40 px-4 py-2.5 text-sm focus:border-maren-green focus:ring-2 focus:ring-maren-gold/30 focus:outline-none"
            />
            <button
              type="submit"
              disabled={status === "submitting"}
              className="rounded-lg bg-maren-green hover:bg-maren-green/90 active:bg-maren-green/80 px-5 py-2.5 text-sm font-semibold text-maren-stone transition focus:outline-none focus:ring-2 focus:ring-maren-gold/50 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {status === "submitting" ? "Adding..." : buttonLabel}
            </button>
          </form>
        )}

        {status === "error" && errorMessage && (
          <p className="mt-3 text-xs text-terracotta dark:text-terracotta">
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
}
